import { InstanceRunningIndicator } from "./InstanceRunningIndicator";
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import * as amplitude from "@amplitude/analytics-browser";

interface InstanceRunningContextType {
  instanceRunning: boolean;
  instanceId: string | null;
  showTimeoutModal: boolean;
  setInstanceDetails: (id: string, startTime: string, duration: number, allowShowTimeoutModal: boolean, onClick: () => void) => void;
}

const InstanceRunningContext = createContext<InstanceRunningContextType>({
  instanceRunning: false,
  instanceId: null,
  showTimeoutModal: false,
  setInstanceDetails: () => {},
});

let InstanceRunningProvider;
let useInstanceRunning;

InstanceRunningProvider = ({ children }) => {
  const location = useLocation(); 

  const [instanceRunning, setInstanceRunning] = useState<boolean>(false);
  const [instanceRunningStartTime, setInstanceRunningStartTime] = useState<number>(0);
  const [instanceDuration, setInstanceDuration] = useState<number>(0);
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [instanceTimeLeft, setInstanceTimeLeft] = useState<number>(0);
  const [showTimeoutModal, setShowTimeoutModal] = useState<boolean>(false);
  const [allowShowTimeoutModal, setAllowShowTimeoutModal] = useState<boolean>(true);
  const [runningInstanceIndicatorOnClick, setRunningInstanceIndicatorOnClick] = useState<() => void>(() => {});

  const setInstanceDetails = useCallback((id: string, startTime: string, durationMs: number, allowShowTimeoutModal: boolean, onClick: () => void) => {
    setInstanceId(id);
    const startTimeMs = Date.parse(startTime);
    setInstanceRunningStartTime(startTimeMs);
    setInstanceDuration(durationMs);
    setAllowShowTimeoutModal(allowShowTimeoutModal);
    setInstanceRunning(true);
    const initialTimeLeft = startTimeMs + durationMs - Date.now();
    setInstanceTimeLeft(initialTimeLeft > 0 ? initialTimeLeft : 0);
    if (initialTimeLeft <= 0) {
      setShowTimeoutModal(allowShowTimeoutModal);
    } else {
      setShowTimeoutModal(false);
    }
    setRunningInstanceIndicatorOnClick(onClick);
  }, []);

  // Timer effect
  useEffect(() => {
    if (!instanceRunning || instanceTimeLeft <= 0) {
      if (instanceId !== null) {
        console.log("unmounting instance", instanceId);
        setInstanceId(null);
        setInstanceRunning(false);
        setInstanceRunningStartTime(0);
        setInstanceDuration(0);
        setInstanceTimeLeft(0);
      }
      return;
    }
    const intervalId = setInterval(() => {
      const currentInstanceId = instanceId;

      setInstanceTimeLeft((prevTimeLeft) => {
        const newTimeLeft = prevTimeLeft - 1000;
        if (newTimeLeft <= 0) {
          clearInterval(intervalId);

          if (currentInstanceId && location.pathname !== `/instance/${currentInstanceId}`) {
            amplitude.track("Lab Abandoned", {
              instance_id: currentInstanceId,
              expired_at: new Date().toISOString(),
            });
          }
          
          setInstanceId(null);
          setInstanceRunning(false);
          setInstanceRunningStartTime(0);
          setInstanceDuration(0);
          setShowTimeoutModal(allowShowTimeoutModal);
          return 0;
        }
        return newTimeLeft;
      });
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceRunning, instanceId, location.pathname]);

  /**
   * Debug details popover
   */
  const contextValue = useMemo(() => ({
    instanceRunning,
    instanceId,
    showTimeoutModal,
    setInstanceDetails,
    setRunningInstanceIndicatorOnClick,
  }), [instanceRunning, instanceId, showTimeoutModal, setInstanceDetails, setRunningInstanceIndicatorOnClick]);

  return (
    <InstanceRunningContext.Provider value={contextValue}>

      <InstanceRunningIndicator
        instanceId={instanceId}
        instanceRunningStartTime={instanceRunningStartTime}
        instanceDuration={instanceDuration}
        instanceTimeLeft={instanceTimeLeft}
        runningInstanceIndicatorOnClick={runningInstanceIndicatorOnClick}
      />
      {children}
    </InstanceRunningContext.Provider>
  );
};

useInstanceRunning = (): InstanceRunningContextType => useContext(InstanceRunningContext);

export { InstanceRunningProvider, useInstanceRunning };
